// bundle-bag-guard.js
// The bundle builder (sections/custom-bundle.liquid) adds the free Pink Bag with the bundle's items,
// all tagged with the same _bundle_id. The bag is only kept while that bundle is complete: if the
// customer removes any of its products, or lowers one below the quantity chosen in the builder,
// the bag is removed from the cart. Raising a quantity is fine.
// A Pink Bag added any other way (product page, direct /cart/add link) has no bundle and is always removed.
(() => {
  const BAG_VARIANT_ID = 52738740256981;        // Free Pink Bag (BG00-TSESP) – the builder's "Free product" setting
  const BAG_PROP = '_bundle_bag';
  const ID_PROP = '_bundle_id';
  const COUNT_PROP = '_bundle_product_count';
  const LINE_COUNT_PROP = '_bundle_line_count'; // on the bag: number of separate products in the bundle
  const LINE_QTY_PROP = '_bundle_qty';          // on each product: quantity chosen in the builder

  // --- helpers ---
  async function getCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
    return res.json();
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify(body)
    });
  }

  const changeLine = (key, quantity) => postJSON('/cart/change.js', { id: key, quantity });

  const prop = (i, name) => (i.properties && i.properties[name]) || null;
  const isBag = (i) => prop(i, BAG_PROP) === 'true' || i.variant_id === BAG_VARIANT_ID;

  function isComplete(cart, bag) {
    const bundleId = prop(bag, ID_PROP);
    if (prop(bag, BAG_PROP) !== 'true' || !bundleId) return false; // not added by the builder

    const lines = cart.items.filter((i) => !isBag(i) && prop(i, ID_PROP) === bundleId);
    const lineCount = parseInt(prop(bag, LINE_COUNT_PROP), 10);

    // Every product from the builder is still there, each at its original quantity or more
    if (lineCount) {
      const intact = lines.filter((i) => i.quantity >= (parseInt(prop(i, LINE_QTY_PROP), 10) || 1));
      return intact.length >= lineCount;
    }

    // Bundles added before _bundle_line_count existed: fall back to the total number of units
    const required = parseInt(prop(bag, COUNT_PROP), 10) || 1;
    return lines.reduce((sum, i) => sum + i.quantity, 0) >= required;
  }

  // Bag lines that need fixing: { line, quantity } where quantity is 0 (remove) or 1 (only one bag per bundle)
  function pendingChanges(cart) {
    return cart.items.filter(isBag).reduce((changes, bag) => {
      const complete = isComplete(cart, bag);

      if (!complete) changes.push({ line: bag, quantity: 0 });
      else if (bag.quantity > 1) changes.push({ line: bag, quantity: 1 });
      return changes;
    }, []);
  }

  let lastCart = null;
  let busy = false;
  let rerun = false;

  // Returns true if the cart was modified
  async function reconcile() {
    if (busy) { rerun = true; return false; }
    busy = true;
    let changed = false;
    try {
      const cart = await getCart();
      // One request at a time: /cart/change.js calls made in parallel can overwrite each other
      for (const change of pendingChanges(cart)) {
        await changeLine(change.line.key, change.quantity);
        changed = true;
      }
      lastCart = changed ? await getCart() : cart;
    } catch (e) {
      console.warn('Bundle bag reconcile error', e);
    } finally {
      busy = false;
    }

    if (changed) {
      // The cart page is server-rendered; everywhere else the theme re-renders the drawer/count on cart:refresh
      if (location.pathname.replace(/\/$/, '').endsWith('/cart')) location.reload();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    if (rerun) { rerun = false; return (await reconcile()) || changed; }
    return changed;
  }

  // --- checkout guard: drop an orphaned bag before leaving for checkout ---
  function isCheckoutEl(el) {
    if (!el) return false;
    if (el.matches('[name="checkout"], .cart__checkout, .checkout-button')) return true;
    if (el.closest('form[action$="/cart"]') && el.matches('[type="submit"]')) return true;
    return el.matches('a[href$="/checkout"], a[href*="/checkout?"]');
  }

  document.addEventListener('click', async (evt) => {
    const el = evt.target.closest('a, button, input[type="submit"]');
    if (!isCheckoutEl(el)) return;
    // Cart already known to be in sync: let the click go through untouched (keeps notes, attributes, etc.)
    if (lastCart && !pendingChanges(lastCart).length) return;

    evt.preventDefault();
    evt.stopPropagation();
    try {
      await reconcile();
    } finally {
      window.location.href = '/checkout'; // fail open
    }
  }, true);

  // --- keep the cart in sync ---
  // The theme dispatches cart:change after every add/update/remove
  let tick;
  const scheduleReconcile = () => { clearTimeout(tick); tick = setTimeout(reconcile, 250); };
  document.addEventListener('cart:change', scheduleReconcile);
  window.addEventListener('pageshow', scheduleReconcile);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile);
  else reconcile();
})();
